import React, { useState, useEffect, useRef } from "react";
import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import { TextareaField } from "../../Common/FormElements/TextareaField";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { createTestimonial } from "../../../services/testimonialService";
const AddTestimonial = () => {
  // Helper function to convert text to URL slug

  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "testimonial/add";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const testimonialNameInputRef = useRef(null);
  const testimonialSubTitleInputRef = useRef(null);
  const testimonialCommentInputRef = useRef(null);
  const displayOrderInputRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    TestimonialName: "",
    TestimonialSubTitle: "",
    TestimonialComment: "",
    Published: false,
    Featured: false,
    DisplayOrder: 1000,
  });

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // ✅ Register all fields
  useEffect(() => {
    registerField("TestimonialName", testimonialNameInputRef, "Name");
    registerField(
      "TestimonialSubTitle",
      testimonialSubTitleInputRef,
      "Sub Title"
    );
    registerField("TestimonialComment", testimonialCommentInputRef, "Comment");
    registerField("DisplayOrder", displayOrderInputRef, "Display Order");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      testimonialNameInputRef,
      testimonialSubTitleInputRef,
      testimonialCommentInputRef,
      displayOrderInputRef,
    };

    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      var responseData = await createTestimonial(formValues);

      // ✅ Reset all fields using refs
      resetAllFields();

      // ✅ Complete form reset
      setFormValues({
        TestimonialName: "",
        TestimonialSubTitle: "",
        TestimonialComment: "",
        Published: false,
        Featured: false,
        DisplayOrder: 1000,
      });

      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Add Testimonial" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Add Testimonial</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Testimonial
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={testimonialNameInputRef}
                      label="Name"
                      id="TestimonialName"
                      value={formValues.TestimonialName}
                      placeholder="Enter Testimonial Name"
                      onChange={handleInputChange}
                      required={true}
                      validationType="alphaonly"
                      maxLength={300}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputField
                      ref={testimonialSubTitleInputRef}
                      label="Sub Title"
                      id="TestimonialSubTitle"
                      value={formValues.TestimonialSubTitle}
                      placeholder="Enter Testimonial Sub Title"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="alphaonly"
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TextareaField
                      ref={testimonialCommentInputRef}
                      label="Comment"
                      id="TestimonialComment"
                      value={formValues.TestimonialComment}
                      onChange={handleInputChange}
                      placeholder="Enter Comment"
                      required={true}
                      showCharCount={true}
                      maxLength={3000}
                    />
                  </div>

                  <div className="col-lg-6 col-md-12 col-sm-12">
                    <InputField
                      ref={displayOrderInputRef}
                      label="Display Order"
                      id="DisplayOrder"
                      value={formValues.DisplayOrder}
                      onChange={handleInputChange}
                      placeholder="Enter Display Order"
                      validationType="numberonly"
                      maxLength={5}
                      required={true}
                    />
                  </div>
                </div>

                <div className="col-lg-6 col-md-12 col-sm-12">
                  <div className="d-flex align-items-center mt-3 mb-3">
                    <label htmlFor="Featured" className="form-label pe-3">
                      Featured Testimonial
                    </label>
                    <div className="form-check form-switch form-switch-custom form-switch-primary">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="Featured"
                        name="Featured"
                        checked={formValues.Featured}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="Featured">
                        {formValues.Featured ? "Yes" : "No"}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddTestimonial;
